// File: src/contexts/PricingSettingsContext.jsx
// Purpose: Studio-level AI & pricing defaults shared across pricing/offer/kiln helpers.

import React, { createContext, useContext, useMemo, useCallback } from "react";

export const defaultPricingSettings = {
  targetRetailMargin: 0.65,
  targetWholesaleMargin: 0.5,
  minHourlyRate: 45,
  enablePricingAi: true,
  enableKilnAi: true,
  enableOfferAi: true,
  enableScheduleAi: true,
  enableHelpAi: true,
};

const PricingSettingsContext = createContext(null);

export function PricingSettingsProvider({ children, settings, setSettings }) {
  const pricingSettings = useMemo(() => {
    const incoming = settings?.pricingSettings;
    return { ...defaultPricingSettings, ...(incoming || {}) };
  }, [settings]);

  const setPricingSettings = useCallback(
    (next) => {
      if (typeof setSettings !== "function") return;
      setSettings((prev) => {
        const prevPricing = { ...defaultPricingSettings, ...(prev?.pricingSettings || {}) };
        const resolved =
          typeof next === "function" ? next(prevPricing) || {} : next || {};
        return {
          ...(prev || {}),
          pricingSettings: { ...defaultPricingSettings, ...resolved },
        };
      });
    },
    [setSettings]
  );

  const value = useMemo(
    () => ({
      pricingSettings,
      setPricingSettings,
    }),
    [pricingSettings, setPricingSettings]
  );

  return (
    <PricingSettingsContext.Provider value={value}>
      {children}
    </PricingSettingsContext.Provider>
  );
}

export function usePricingSettings() {
  const ctx = useContext(PricingSettingsContext);
  if (!ctx) throw new Error("usePricingSettings must be used within PricingSettingsProvider");
  return ctx;
}
